<template>
    <div
        class="qa-drawer-header__action-items-container"
        :class="{ 'qa-drawer-header__action-items-container--bulk': isBulk }"
    >
        <div class="qa-drawer-header__action-buttons">
            <div class="qa-drawer-header__action-item">
                <Button
                    :disabled="buttonsDisabled"
                    ghost
                    :loading="isApprovingAllDeliverables"
                    type="success"
                    data-testid="qa-drawer__button--approve"
                    @click="approveAllDeliverables"
                >
                    Approve page
                </Button>
            </div>
            <div class="qa-drawer-header__action-item">
                <Button
                    ghost
                    :disabled="buttonsDisabled"
                    :loading="isRejectingAllDeliverables"
                    type="error"
                    data-testid="qa-drawer__button--reject"
                    @click="rejectAllDeliverables"
                >
                    Reject page
                </Button>
            </div>
            <div v-if="userCanChangeDeliverableQAStatus" class="qa-drawer-header__action-item">
                <SetDeliverablesStatusDropdown
                    :deliverables="visibleDeliverables"
                    :is-disabled="buttonsDisabled"
                    :library-filters-for-query="null"
                    data-testid="qa-drawer__select--production-status"
                    @statusHasBeenSet="$emit('statusHasBeenUpdated')"
                />
            </div>
            <div v-if="userCanChangeDeliverableVisibility" class="qa-drawer-header__action-item">
                <SetDeliverablesVisibleToDropdown
                    :deliverables="visibleDeliverables"
                    :library-filters-for-query="null"
                    :is-disabled="buttonsDisabled"
                    data-testid="qa-drawer__select--visibility"
                >
                    Visibility
                </SetDeliverablesVisibleToDropdown>
            </div>
            <div class="qa-drawer-header__action-item">
                <portal-target name="creativeSizeFilter" />
            </div>
        </div>
        <div v-if="isBulk" class="qa-drawer-header__bulk-page">
            <Page :model-value="bulkCurrent" :page-size="1" :total="bulkTotal" @onChange="onPageChange" />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { BannerStatus } from "shared-types";

import { BannerQaStatus } from "@/enums/banners";
import SetDeliverablesStatusDropdown from "@/components/Campaign/SetDeliverablesStatusDropdown.vue";
import SetDeliverablesVisibleToDropdown from "@/components/Campaign/SetDeliverablesVisibleToDropdown.vue";
import { setLoggedInUsersDeliverablesStatus } from "@/services/Qa";
import { DeliverableLibraryAction } from "@/store/modules/deliverableLibrary";
import type { ConstructedDeliverable } from "@/types/types";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";
import { userCan } from "@/services/Auth";
import { USER_ACTIONS } from "shared-utils/enums/user";

export default defineComponent({
    components: {
        SetDeliverablesStatusDropdown,
        SetDeliverablesVisibleToDropdown
    },
    props: {
        bulkCurrent: {
            type: Number,
            default: 1
        },
        bulkTotal: {
            type: Number,
            default: 1
        },
        buttonsDisabled: {
            type: Boolean
        },
        deliverables: {
            type: Array as PropType<ConstructedDeliverable[]>,
            required: true
        },
        isBulk: {
            type: Boolean,
            default: false
        },
        visibleMasterTemplateIds: {
            type: Array as PropType<string[]>,
            required: true
        }
    },

    emits: ["userQaStatusHasBeenUpdated", "bulkIndexChange", "statusHasBeenUpdated"],

    setup() {
        const ga4 = useGA4Tracking();

        return {
            ga4
        };
    },

    data() {
        return {
            isApprovingAllDeliverables: false,
            isRejectingAllDeliverables: false
        };
    },

    computed: {
        campaignId() {
            return this.$route.params.campaignId as string;
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        visibleDeliverables() {
            if (!this.visibleMasterTemplateIds.length) {
                return this.deliverables;
            }

            return this.deliverables.filter(({ masterTemplate }) =>
                this.visibleMasterTemplateIds.includes(masterTemplate._id)
            );
        },

        userCanChangeDeliverableQAStatus() {
            return userCan(USER_ACTIONS.canChangeDeliverableQAStatus, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        userCanChangeDeliverableVisibility() {
            return userCan(USER_ACTIONS.canChangeDeliverableVisibility, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        }
    },

    methods: {
        async approveAllDeliverables() {
            this.ga4.trackClickEvent({
                interactionObject: GA4.CLICK_INTERACTION_OBJECTS.BUTTON,
                interactionText: GA4.CLICK_INTERACTION_TEXTS.APPROVE_ALL_DELIVERABLES
            });

            this.isApprovingAllDeliverables = true;

            const deliverablesCount = this.visibleDeliverables.length;

            try {
                await this.setDeliverablesUserStatus(this.visibleDeliverables, BannerQaStatus.Approved);

                if (deliverablesCount === 1) {
                    this.$snackbar.success("You have approved the deliverable.");
                } else {
                    this.$snackbar.success(`You have approved ${deliverablesCount} deliverables.`);
                }

                this.$emit("userQaStatusHasBeenUpdated");
            } catch {
                this.$snackbar.error("Sorry, We could not approve your ads this time. Try again in a moment.");
            }

            this.isApprovingAllDeliverables = false;
        },

        onPageChange(page: number) {
            this.$emit("bulkIndexChange", page);
        },

        async setDeliverablesUserStatus(deliverables: ConstructedDeliverable[], status: BannerStatus) {
            const updatedDeliverables = await setLoggedInUsersDeliverablesStatus(this.campaignId, deliverables, status);
            this.$store.dispatch(DeliverableLibraryAction.SetDeliverableStatusByIdHash, updatedDeliverables);
        },

        async rejectAllDeliverables() {
            this.ga4.trackClickEvent({
                interactionObject: GA4.CLICK_INTERACTION_OBJECTS.BUTTON,
                interactionText: GA4.CLICK_INTERACTION_TEXTS.REJECT_ALL_DELIVERABLES
            });
            this.isRejectingAllDeliverables = true;

            const deliverablesCount = this.visibleDeliverables.length;

            try {
                await this.setDeliverablesUserStatus(this.visibleDeliverables, BannerQaStatus.Rejected);

                if (deliverablesCount === 1) {
                    this.$snackbar.success("Deliverable rejected.");
                } else {
                    this.$snackbar.success(`${deliverablesCount} deliverables rejected.`);
                }

                this.$emit("userQaStatusHasBeenUpdated");
            } catch {
                this.$snackbar.error("Sorry, We could not reject your ads this time. Try again in a moment.");
            }

            this.isRejectingAllDeliverables = false;
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.qa-drawer-header__action-items-container {
    display: flex;
    align-items: center;
    flex-direction: column;
    margin: 0 0 $spacing;

    .qa-drawer-header__action-buttons {
        margin-bottom: 0;
        align-self: flex-start;
    }

    &--bulk {
        .qa-drawer-header__action-buttons {
            margin-bottom: $spacing;
        }
    }

    .ivu-icon-ios-loading {
        position: absolute;
        right: calc(50% - 10px);
        top: calc(50% - 10px);
    }

    .ivu-btn > .ivu-icon + span {
        margin-left: 0;
    }
}

.qa-drawer-header__action-item {
    display: inline-block;
    margin: 12px $spacing-small 0 0;
}

.qa-drawer-header__action-item--extra-spacing {
    display: inline-block;
    margin: 0 $spacing-large 0 0;
}

@media all and (min-width: 1600px) {
    .qa-drawer-header__action-items-container {
        flex-direction: row;

        .qa-drawer-header__action-buttons {
            margin-bottom: 0;
        }

        .qa-drawer-header__bulk-page {
            margin-top: 12px;
            margin-left: auto;
            margin-right: 0;
        }
    }
}
</style>
